// SPDX-License-Identifier: AGPL-3.0-or-later

import * as LobbyCommands from '@app/features/lobby/commands/LobbyCommands';
import {LOBBY_COPY} from '@app/features/lobby/utils/LobbyCopy';
import {GenericErrorModal} from '@app/features/app/components/alerts/GenericErrorModal';
import {CheckboxItem} from '@app/features/ui/action_menu/ContextMenu';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {AdminACLs} from '@fluxer/constants/src/AdminACLs';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

interface LobbyCreateRestrictionMenuItemProps {
	user: User;
}

export function shouldShowLobbyModerationMenuItems(params: {
	currentUser: User | null | undefined;
	user: User;
	isCurrentUser: boolean;
}): boolean {
	return (
		!params.isCurrentUser &&
		!params.user.bot &&
		(params.currentUser?.hasAdminAcl(AdminACLs.LOBBY_MODERATE) ?? false)
	);
}

export const LobbyCreateRestrictionMenuItem: React.FC<LobbyCreateRestrictionMenuItemProps> = observer(({user}) => {
	const {i18n} = useLingui();
	const [restricted, setRestricted] = useState(false);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		void LobbyCommands.fetchLobbyCreateRestriction(user.id)
			.then((result) => {
				if (!cancelled) setRestricted(result.restricted);
			})
			.catch(() => {
				if (!cancelled) setRestricted(false);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [user.id]);

	const handleChange = useCallback(
		(checked: boolean) => {
			if (busy || loading) return;
			setBusy(true);
			const previous = restricted;
			setRestricted(checked);
			void LobbyCommands.setLobbyCreateRestriction(user.id, checked)
				.then((result) => {
					setRestricted(result.restricted);
					const current = Users.currentUser;
					if (current?.id === user.id) {
						// Self updates arrive via USER_UPDATE; no local patch needed.
					}
				})
				.catch(() => {
					setRestricted(previous);
					ModalCommands.push(
						modal(() => (
							<GenericErrorModal
								title={i18n._(LOBBY_COPY.actionFailed)}
								message={i18n._(LOBBY_COPY.actionFailed)}
							/>
						)),
					);
				})
				.finally(() => setBusy(false));
		},
		[busy, loading, restricted, user.id, i18n],
	);

	return (
		<CheckboxItem checked={restricted} disabled={loading || busy} onCheckedChange={handleChange}>
			{i18n._(LOBBY_COPY.restrictLobbyCreate)}
		</CheckboxItem>
	);
});
